import type { Partner } from "@expert/shared-types";

import { getLogger } from "@expert/logging";

import { getRootDispatcher } from "@/analytics";
import { getGamificationApiClient } from "@/sales-tools/api/api";

import type { PayMetrics } from "./types";

import { emitExpertHubError, EXPERT_HUB_ACTIONS } from "./expertHubErrorMetric";

const logger = getLogger({ module: "getPayMetrics" });

function isPayMetrics(data: unknown): data is PayMetrics {
    if (typeof data !== "object" || data === null) return false;
    const obj = data as Record<string, unknown>;
    if (!("totals" in obj)) return false;
    const totals = obj.totals;
    if (typeof totals !== "object" || totals === null) return false;
    return "serve" in totals && "solve" in totals && "sell" in totals;
}

export const getPayMetrics = async (partner?: Partner): Promise<PayMetrics | null> => {
    const resp: unknown = await getGamificationApiClient().get("v1/pay-metrics").json();

    // Emit here, not in the useQuery consumers: this runs once per actual fetch (React Query dedupes
    // concurrent callers by key, and a remount inside staleTime is a cache hit that never re-runs it),
    // so the metric can't be multiplied by the 3 co-mounted usePayMetrics callers or by remounts.
    if (!isPayMetrics(resp)) {
        logger.warn(
            { responseKeys: resp ? Object.keys(resp as object) : null },
            "Off-call hub: pay-metrics API response did not match expected shape",
        );
        // A 200 whose body fails the shape guard is a fetch failure the reject path never sees.
        emitExpertHubError(EXPERT_HUB_ACTIONS.payMetricsFetchFailed, partner);
        return null;
    }

    const hasServe = Object.keys(resp.totals.serve).length > 0;
    const hasSolve = Object.keys(resp.totals.solve).length > 0;
    const hasSell = Object.keys(resp.totals.sell).length > 0;

    void getRootDispatcher().dispatchBusinessEvent("PayMetricsReceived", { hasServe, hasSolve, hasSell });

    // OK response carrying no usable data → cards render blank despite a "success".
    if (!hasServe && !hasSolve && !hasSell) {
        emitExpertHubError(EXPERT_HUB_ACTIONS.payMetricsEmpty, partner);
    }

    return resp;
};
